import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useCoachPendingTranscriptReviewCount } from "@/hooks/useCoachPendingTranscriptReviewCount";
import { useCoachDevelopmentUpdates } from "@/hooks/useCoachDevelopment";
import { coachDevelopmentDestination, type DevelopmentUpdate } from "@/lib/coachDevelopment";
import { useCoachSessionUpdates } from "@/hooks/useCoachSessionUpdates";
import { coachUpdateDestination, type CoachSessionUpdate } from "@/lib/coachSessionFollowThrough";
import { useUnreadMessages } from "@/hooks/useUnreadMessages";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

// ── Types ─────────────────────────────────────────────────────────────────────

export type NotificationSeverity = "high" | "medium" | "low";

export interface CoachNotificationItem {
  id: string;
  kind: "table" | "derived" | "session" | "development";
  sessionUpdate?: CoachSessionUpdate;
  developmentUpdate?: DevelopmentUpdate;
  severity: NotificationSeverity;
  title: string;
  description: string;
  route: string;
  read: boolean;
  createdAt: string | null;
}

/** Raw row shape from coach_notifications */
interface CoachNotificationRow {
  id: string;
  coach_id: string;
  leader_id: string;
  leader_name: string;
  type: string;
  title: string;
  message: string;
  action_url: string | null;
  read: boolean | null;
  read_at: string | null;
  created_at: string;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const QUERY_KEY_PREFIX = "coach-notifications";
const MAX_ITEMS = 5;
const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

// Multi-tab note: localStorage last-writer-wins is accepted for v1.
/** Derived-item read-state stored in localStorage */
const DERIVED_READ_KEY = (userId: string) =>
  `coach-derived-notification-read-v1:${userId}`;

// ── Severity mapping from notification type ───────────────────────────────────

function typeToSeverity(type: string): NotificationSeverity {
  switch (type) {
    case "analysis_ready":
    case "attention_needed":
    case "continuity_shift":
      return "high";
    case "new_message":
    case "session_captured":
    case "insight_available":
      return "medium";
    default:
      return "medium";
  }
}

// ── Derived read-state helpers ────────────────────────────────────────────────

function readDerivedReadMap(userId: string): Record<string, number> {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(DERIVED_READ_KEY(userId));
    if (!raw) return {};
    return JSON.parse(raw) as Record<string, number>;
  } catch {
    return {};
  }
}

function writeDerivedReadMap(userId: string, map: Record<string, number>) {
  if (typeof window === "undefined") return;
  try {
    if (Object.keys(map).length === 0) {
      window.localStorage.removeItem(DERIVED_READ_KEY(userId));
      return;
    }
    window.localStorage.setItem(DERIVED_READ_KEY(userId), JSON.stringify(map));
  } catch {
    // storage unavailable — ignore
  }
}

function normalizeDerivedReadMap(
  map: Record<string, number>,
  counts: Record<"derived:pending-reviews" | "derived:unread-messages", number>,
): Record<string, number> {
  const next = { ...map };
  for (const [key, count] of Object.entries(counts)) {
    if (count <= 0) {
      delete next[key];
    }
  }
  return next;
}

// ── Query function ─────────────────────────────────────────────────────────────

async function fetchCoachNotifications(
  userId: string,
  dbClient: typeof db = db,
): Promise<CoachNotificationRow[]> {
  const cutoff = new Date(Date.now() - TWENTY_FOUR_HOURS_MS).toISOString();

  const { data, error } = await dbClient
    .from("coach_notifications")
    .select("id, coach_id, leader_id, leader_name, type, title, message, action_url, read, read_at, created_at")
    .eq("coach_id", userId)
    .or(`read.is.null,read.eq.false,read_at.gte.${cutoff}`)
    .order("created_at", { ascending: false })
    .limit(10);

  if (error) {
    console.error("[useCoachNotificationItems] fetch error", error);
    return [];
  }

  return (data || []) as CoachNotificationRow[];
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useCoachNotificationItems(userId?: string) {
  const { user } = useAuth();
  const effectiveUserId = userId ?? user?.id;

  // Counter to force re-computation when localStorage-backed derived read-state changes.
  const [derivedReadVersion, setDerivedReadVersion] = useState(0);
  const bumpDerivedReadVersion = useCallback(() => setDerivedReadVersion((v) => v + 1), []);

  const queryClient = useQueryClient();

  // 1. Table-backed notifications
  const notificationsQuery = useQuery({
    queryKey: [QUERY_KEY_PREFIX, effectiveUserId],
    enabled: Boolean(effectiveUserId),
    queryFn: () => fetchCoachNotifications(effectiveUserId!, db),
    staleTime: 30_000,
  });

  // 2. Pending transcript reviews (existing hook)
  const {
    leadersWithPendingTranscriptReviewCount: pendingReviewCount,
    isLoadingPendingTranscriptReviewCount,
  } = useCoachPendingTranscriptReviewCount();

  // 3. Unread messages (existing hook)
  const unreadMessagesCount = useUnreadMessages();
  const {updates:sessionUpdates,isError:sessionUpdatesUnavailable,refetch:retrySessionUpdates} = useCoachSessionUpdates(effectiveUserId);

  const {updates:developmentUpdates,isError:developmentUpdatesUnavailable,refetch:retryDevelopmentUpdates} = useCoachDevelopmentUpdates(effectiveUserId);

  useEffect(() => {
    if (!effectiveUserId || isLoadingPendingTranscriptReviewCount) return;
    const rawDerivedMap = readDerivedReadMap(effectiveUserId);
    const derivedMap = normalizeDerivedReadMap(rawDerivedMap, {
      "derived:pending-reviews": pendingReviewCount,
      "derived:unread-messages": unreadMessagesCount,
    });
    if (JSON.stringify(derivedMap) !== JSON.stringify(rawDerivedMap)) {
      writeDerivedReadMap(effectiveUserId, derivedMap);
      bumpDerivedReadVersion();
    }
  }, [
    effectiveUserId,
    isLoadingPendingTranscriptReviewCount,
    pendingReviewCount,
    unreadMessagesCount,
    bumpDerivedReadVersion,
  ]);

  // ── Build unified item list ──────────────────────────────────────────────

  // derivedReadVersion is intentionally in deps to force re-computation when
  // localStorage derived read-state changes without a query cache change.
  const items = useMemo<CoachNotificationItem[]>(() => {
    void derivedReadVersion; // consumed to satisfy exhaustive-deps
    const rows = notificationsQuery.data ?? [];

    // Deduplicate table rows: keep only the newest per (leader_id, type)
    const dedupeMap = new Map<string, CoachNotificationRow>();
    for (const row of rows) {
      const key = `${row.leader_id}:${row.type}`;
      if (!dedupeMap.has(key)) {
        dedupeMap.set(key, row);
      }
    }
    const dedupedRows = Array.from(dedupeMap.values());

    // Map to unified shape
    const tableItems: CoachNotificationItem[] = dedupedRows.map((row) => ({
      id: row.id,
      kind: "table",
      severity: typeToSeverity(row.type),
      title: row.title,
      description: row.message,
      route: row.action_url ?? "/coach/overview",
      // Coerce: NULL is treated as unread (same as false).
      read: row.read === true,
      createdAt: row.created_at,
    }));

    // Derived items — read-state from localStorage.
    // Guard: don't emit derived items while source counts are still loading
    // to avoid read-state flicker (the count would show 0 then jump to real value).
    const derivedItems: CoachNotificationItem[] = [];

    if (!isLoadingPendingTranscriptReviewCount) {
      const derivedMap = effectiveUserId
        ? readDerivedReadMap(effectiveUserId)
        : {};

      if (pendingReviewCount > 0) {
        const readAt = derivedMap["derived:pending-reviews"];
        derivedItems.push({
          id: "derived:pending-reviews",
          kind: "derived",
          severity: "high",
          title: "Session analysis ready",
          description: `${pendingReviewCount} leader${pendingReviewCount === 1 ? "" : "s"} have sessions waiting for review`,
          route: "/coach/reviews",
          read: readAt !== undefined && pendingReviewCount === readAt,
          createdAt: null,
        });
      }

      if (unreadMessagesCount > 0) {
        const readAt = derivedMap["derived:unread-messages"];
        derivedItems.push({
          id: "derived:unread-messages",
          kind: "derived",
          severity: "medium",
          title: "Unread leader messages",
          description: `${unreadMessagesCount} unread message${unreadMessagesCount === 1 ? "" : "s"}`,
          route: "/coach/messages",
          read: readAt !== undefined && unreadMessagesCount === readAt,
          createdAt: null,
        });
      }
    }

    // Merge and sort: severity (high→medium→low) then createdAt desc (nulls last)
    const severityOrder: Record<NotificationSeverity, number> = {
      high: 0,
      medium: 1,
      low: 2,
    };

    const sessionItems: CoachNotificationItem[] = sessionUpdates.map(update => ({
      id: `session:${update.relationshipRevision}:${update.recordId}:${update.publicationId ?? update.agreementDigest}:${update.itemId}:${update.revision}`,
      kind: 'session', severity: 'medium', title: update.kind === 'step' ? 'Leader next-step check-in' : 'Leader feedback response',
      description: `${update.leaderName} shared an update. Open the session to review it.`, route: coachUpdateDestination(update), read: false, createdAt: update.updatedAt, sessionUpdate: update,
    }));
    const developmentItems: CoachNotificationItem[] = developmentUpdates.map(update => ({
      id: `development:${update.relationshipRevision}:${update.publicationId}:${update.revision}`,
      kind: 'development', severity: 'medium', title: 'Leader perspective on a connection',
      description: `${update.leaderName} shared a perspective. Open the comparison to review it.`, route: coachDevelopmentDestination(update.leaderId, update.publicationId),
      read: false, createdAt: update.updatedAt, developmentUpdate: update,
    }));
    const all = [...tableItems, ...derivedItems, ...sessionItems, ...developmentItems].sort((a, b) => {
      if (a.read !== b.read) return a.read ? 1 : -1;
      const sev = severityOrder[a.severity] - severityOrder[b.severity];
      if (sev !== 0) return sev;
      if (!a.createdAt && !b.createdAt) return 0;
      if (!a.createdAt) return 1;
      if (!b.createdAt) return -1;
      return b.createdAt.localeCompare(a.createdAt);
    });

    return all.slice(0, MAX_ITEMS);
  }, [
    derivedReadVersion,
    notificationsQuery.data,
    pendingReviewCount,
    isLoadingPendingTranscriptReviewCount,
    unreadMessagesCount,
    effectiveUserId,
    sessionUpdates,
    developmentUpdates,
  ]);

  const unreadCount = useMemo(
    () => items.filter((item) => !item.read).length,
    [items],
  );

  // ── markRead ──────────────────────────────────────────────────────────────

  const markRead = useCallback(
    async (item: CoachNotificationItem) => {
      if (item.read) return;

      if (item.kind === "session" || item.kind === "development") return; // Source views acknowledge only displayed revisions.
      if (item.kind === "table") {
        const { error } = await db
          .from("coach_notifications")
          .update({ read: true, read_at: new Date().toISOString() })
          .eq("id", item.id);
        if (error) {
          console.error("[coach-notifications] failed to mark read", error);
          return;
        }
        queryClient.invalidateQueries({ queryKey: [QUERY_KEY_PREFIX, effectiveUserId] });
      } else {
        // Derived item — persist the count it was read at
        if (!effectiveUserId) return;
        const map = readDerivedReadMap(effectiveUserId);
        if (item.id === "derived:pending-reviews") {
          map["derived:pending-reviews"] = pendingReviewCount;
        } else if (item.id === "derived:unread-messages") {
          map["derived:unread-messages"] = unreadMessagesCount;
        }
        writeDerivedReadMap(effectiveUserId, map);
        // Bump the version counter to force useMemo re-computation with new localStorage state.
        bumpDerivedReadVersion();
      }
    },
    [effectiveUserId, pendingReviewCount, queryClient, unreadMessagesCount, bumpDerivedReadVersion],
  );

  // ── markAllRead ───────────────────────────────────────────────────────────

  const markAllRead = useCallback(async () => {
    if (!effectiveUserId) return;

    const { error } = await db
      .from("coach_notifications")
      .update({ read: true, read_at: new Date().toISOString() })
      .or("read.is.null,read.eq.false")
      .eq("coach_id", effectiveUserId);
    if (error) {
      console.error("[coach-notifications] failed to mark read", error);
      return;
    }
    queryClient.invalidateQueries({ queryKey: [QUERY_KEY_PREFIX, effectiveUserId] });

    // Mark derived items read at their current counts
    const map = readDerivedReadMap(effectiveUserId);
    if (pendingReviewCount > 0) {
      map["derived:pending-reviews"] = pendingReviewCount;
    }
    if (unreadMessagesCount > 0) {
      map["derived:unread-messages"] = unreadMessagesCount;
    }
    writeDerivedReadMap(effectiveUserId, map);
    // Bump version to force useMemo re-computation with new localStorage state.
    bumpDerivedReadVersion();
  }, [
    effectiveUserId,
    pendingReviewCount,
    unreadMessagesCount,
    queryClient,
    bumpDerivedReadVersion,
  ]);

  const retryUpdates = useCallback(() => Promise.allSettled([retrySessionUpdates(), retryDevelopmentUpdates()]), [retrySessionUpdates, retryDevelopmentUpdates]);
  return useMemo(
    () => ({ items, unreadCount, markRead, markAllRead, updatesUnavailable: sessionUpdatesUnavailable || developmentUpdatesUnavailable, retryUpdates }),
    [items, unreadCount, markRead, markAllRead, sessionUpdatesUnavailable, developmentUpdatesUnavailable, retryUpdates],
  );
}
